import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Smile } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import {
  abrirConversaAreaCliente,
  abrirConversaPlataformaIA,
  encerrarConversaAreaCliente,
  enviarMensagemAreaCliente,
  obterAgenteAreaCliente,
  sinalizarDigitandoAreaCliente,
  obterConversaAreaCliente,
  perguntarPlataformaIA,
  responderAvaliacaoAreaCliente,
  type AvaliacaoChatAreaCliente,
  type AnexoMensagemIA,
  type DestinoChatAreaCliente,
  type MensagemIA,
} from "../../api/agenteIa";
import { obterDadosPerfilUsuario } from "../../api/usuario";
import { urlAsset } from "../../utils/assets";
import { BancoImage } from "../ui/BancoImage";
import { BotaoFechar } from "../ui/BotaoFechar";
import { CampoPesquisa } from "../ui/CampoPesquisa";
import { IconeChatGPT } from "../icons";
import { PainelEmojis } from "../ui/SeletorEmoji";
import { useIdioma } from "../../i18n/IdiomaProvider";
import { mostrarToastErro } from "../../utils/toast";
import styles from "./AgenteIA.module.css";

interface Mensagem {
  id?: number;
  texto: string;
  tipo: "user" | "bot";
  anexos?: AnexoMensagemIA[];
}

const MENSAGEM_INICIAL_AREA_CLIENTE = "Olá! Como podemos ajudar? Escolha uma opção ou escreva sua dúvida abaixo.";

/* Ordem dos atendimentos oferecidos ao cliente. Cada um só entra na tela quando
   a empresa tem login com o acesso correspondente no cadastro. */
const OPCOES_ATENDIMENTO: Array<{ destino: DestinoChatAreaCliente; rotulo: string }> = [
  { destino: "SUPORTE", rotulo: "Suporte técnico" },
  { destino: "VENDAS", rotulo: "Comercial" },
  { destino: "PRODUCAO", rotulo: "Vendas DTF" },
  { destino: "DUVIDAS", rotulo: "Dúvidas" },
];

/* Pesquisa exibida no fim do atendimento. No WhatsApp o cliente recebe um link;
   aqui ela é respondida dentro da própria conversa. */
const NOTAS_AVALIACAO = [
  { valor: 1, emoji: "😠", rotulo: "Muito ruim" },
  { valor: 2, emoji: "🙁", rotulo: "Ruim" },
  { valor: 3, emoji: "😐", rotulo: "Regular" },
  { valor: 4, emoji: "🙂", rotulo: "Bom" },
  { valor: 5, emoji: "😍", rotulo: "Excelente" },
];

const IconeEnviar = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
);

const IconeClipe = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="m21.4 11-8.9 8.9a6 6 0 1 1-8.5-8.5l9.2-9.2a4 4 0 0 1 5.7 5.7l-9.2 9.2a2 2 0 1 1-2.8-2.8l8.2-8.2" />
  </svg>
);

const IconeVoltar = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
);

function arquivoDataUrl(arquivo: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(String(leitor.result || ""));
    leitor.onerror = () => reject(new Error("Não foi possível ler a imagem."));
    leitor.readAsDataURL(arquivo);
  });
}

interface AgenteIAProps {
  aberto: boolean;
  onFechar: () => void;
  modo?: "PLATAFORMA" | "AREA_CLIENTE";
}

export function AgenteIA({ aberto, onFechar, modo = "PLATAFORMA" }: AgenteIAProps) {
  const { usuario } = useAuth();
  const { t } = useIdioma();
  const [nome, setNome] = useState(t("assistantIa"));
  const [avatar, setAvatar] = useState("");
  const [avatarUsuario, setAvatarUsuario] = useState("");
  const [nomeUsuarioAtual, setNomeUsuarioAtual] = useState("");
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [conversaId, setConversaId] = useState<number | null>(null);
  const [texto, setTexto] = useState("");
  const [digitando, setDigitando] = useState(false);
  const [destino, setDestino] = useState<DestinoChatAreaCliente | null>(null);
  const [perguntasRapidas, setPerguntasRapidas] = useState<string[]>([]);
  const [avaliacao, setAvaliacao] = useState<AvaliacaoChatAreaCliente | null>(null);
  const [notaEscolhida, setNotaEscolhida] = useState(0);
  const [comentarioAvaliacao, setComentarioAvaliacao] = useState("");
  const [enviandoAvaliacao, setEnviandoAvaliacao] = useState(false);
  const [encerrando, setEncerrando] = useState(false);
  const [imagem, setImagem] = useState<{ arquivo: File; preview: string } | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [emojiAberto, setEmojiAberto] = useState(false);
  const botaoEmojiRef = useRef<HTMLButtonElement | null>(null);
  const ancoraEmojisRef = useRef<HTMLDivElement | null>(null);
  const [posicaoEmojis, setPosicaoEmojis] = useState<{ left: number; bottom: number } | null>(null);
  /* Pontinhos enquanto o atendente humano escreve do outro lado. */
  const [atendenteDigitando, setAtendenteDigitando] = useState(false);
  const ultimoSinalDigitandoRef = useRef(0);
  /* Sem login com o acesso do canal, o botão daquele atendimento não aparece. */
  const [destinosDisponiveis, setDestinosDisponiveis] = useState<DestinoChatAreaCliente[]>(["DUVIDAS"]);
  const corpoRef = useRef<HTMLDivElement>(null);
  const inputArquivoRef = useRef<HTMLInputElement>(null);
  const arrasto = useRef<{ dx: number; dy: number } | null>(null);

  useEffect(() => {
    setNome((atual) => (atual ? atual : t("assistantIa")));
  }, [t]);

  useEffect(() => {
    if (!aberto) return;
    let ativo = true;
    setConversaId(null);
    setDestino(null);
    setPerguntasRapidas([]);
    setAvaliacao(null);
    setNotaEscolhida(0);
    setComentarioAvaliacao("");
    setTexto("");
    setImagem(null);
    setDigitando(false);
    if (modo === "AREA_CLIENTE") {
      setNome("Assistente");
      setAvatar("");
      setMensagens([{ tipo: "bot", texto: MENSAGEM_INICIAL_AREA_CLIENTE }]);
      /* O nome e a foto vêm das configurações do agente e já aparecem na tela
         de escolha do atendimento, antes de existir conversa. */
      void obterAgenteAreaCliente()
        .then((dados) => {
          if (!ativo) return;
          aplicarIdentidadeAgente(dados.agente);
          setDestinosDisponiveis(dados.destinos);
        })
        .catch(() => null);
      return () => { ativo = false; };
    }
    void (async () => {
      try {
        const detalhe = await abrirConversaPlataformaIA();
        if (!ativo) return;
        setConversaId(detalhe.conversa.id);
        setPerguntasRapidas(detalhe.perguntas || []);
        aplicarIdentidadeAgente(detalhe.agente);
        setMensagens(detalhe.mensagens.filter((item) => item.autor_tipo !== "SISTEMA").map(converterMensagem));
      } catch (erro) {
        if (ativo) mostrarToastErro(erro instanceof Error ? erro.message : t("assistantLoadError"));
      }
    })();
    return () => { ativo = false; };
  }, [aberto, modo, t]);

  useEffect(() => {
    if (!usuario) {
      setAvatarUsuario("");
      setNomeUsuarioAtual("");
      return;
    }
    let ativo = true;
    const nomeBase = (usuario.email?.split("@")[0] || usuario.email || "Usuario").trim();
    setNomeUsuarioAtual(nomeBase ? nomeBase.charAt(0).toUpperCase() + nomeBase.slice(1) : "Usuario");
    setAvatarUsuario(urlAsset(usuario.avatarId || ""));
    void (async () => {
      try {
        const perfil = await obterDadosPerfilUsuario();
        if (!ativo) return;
        setAvatarUsuario(perfil?.urlImagem || urlAsset(usuario.avatarId || ""));
        if (perfil?.nomePerfil?.trim()) setNomeUsuarioAtual(perfil.nomePerfil.trim());
      } catch {
        if (!ativo) return;
        setAvatarUsuario(urlAsset(usuario.avatarId || ""));
      }
    })();
    return () => {
      ativo = false;
    };
  }, [usuario]);

  useEffect(() => {
    if (corpoRef.current) corpoRef.current.scrollTop = corpoRef.current.scrollHeight;
  }, [mensagens, digitando, aberto]);

  useEffect(() => {
    if (!aberto || modo !== "AREA_CLIENTE" || !destino || !conversaId) return;
    const atualizar = () => {
      void obterConversaAreaCliente(conversaId, destino).then((detalhe) => {
        setMensagens(detalhe.mensagens.map(converterMensagem));
        aplicarIdentidadeAgente(detalhe.agente);
        setAvaliacao(detalhe.avaliacao || null);
        setAtendenteDigitando(detalhe.conversa.digitando_atendente === true);
      }).catch(() => null);
    };
    const intervalo = window.setInterval(atualizar, 5_000);
    return () => window.clearInterval(intervalo);
  }, [aberto, conversaId, destino, modo]);

  const inicialUsuario = (nomeUsuarioAtual.trim()[0] || usuario?.email?.trim()[0] || "U").toUpperCase();

  /** Sinaliza a digitação do cliente, no máximo uma vez a cada 3 segundos. */
  function avisarDigitando() {
    if (modo !== "AREA_CLIENTE" || !conversaId || !destino) return;
    const agora = Date.now();
    if (agora - ultimoSinalDigitandoRef.current < 3_000) return;
    ultimoSinalDigitandoRef.current = agora;
    void sinalizarDigitandoAreaCliente(conversaId, destino).catch(() => null);
  }

  /**
   * Abre o painel ancorado no botão: alinhado pela direita (cresce para a
   * esquerda) e acima da barra de mensagem, sempre dentro da janela.
   */
  function alternarEmojis() {
    if (emojiAberto) {
      setEmojiAberto(false);
      return;
    }
    const retangulo = botaoEmojiRef.current?.getBoundingClientRect();
    if (!retangulo) return;
    const largura = Math.min(360, window.innerWidth - 32);
    const left = Math.max(12, Math.min(retangulo.right - largura, window.innerWidth - largura - 12));
    const bottom = Math.max(12, window.innerHeight - retangulo.top + 8);
    setPosicaoEmojis({ left, bottom });
    setEmojiAberto(true);
  }

  useEffect(() => {
    if (!emojiAberto) return undefined;
    const aoClicarFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      if (ancoraEmojisRef.current?.contains(alvo) || botaoEmojiRef.current?.contains(alvo)) return;
      setEmojiAberto(false);
    };
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === "Escape") setEmojiAberto(false);
    };
    document.addEventListener("mousedown", aoClicarFora);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("mousedown", aoClicarFora);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [emojiAberto]);

  function aplicarIdentidadeAgente(agente: { nome: string; imagem_id?: string }) {
    setNome(agente.nome || "Assistente");
    setAvatar(agente.imagem_id ? urlAsset(agente.imagem_id) : "");
  }

  function aplicarConversaAreaCliente(detalhe: Awaited<ReturnType<typeof abrirConversaAreaCliente>>) {
    setConversaId(detalhe.conversa.id);
    aplicarIdentidadeAgente(detalhe.agente);
    setMensagens(detalhe.mensagens.map(converterMensagem));
    setAvaliacao(detalhe.avaliacao || null);
  }

  async function enviarAvaliacao() {
    if (!conversaId || !destino || !notaEscolhida || enviandoAvaliacao) return;
    setEnviandoAvaliacao(true);
    try {
      const detalhe = await responderAvaliacaoAreaCliente(conversaId, destino, notaEscolhida, comentarioAvaliacao.trim());
      setMensagens(detalhe.mensagens.map(converterMensagem));
      setAvaliacao(detalhe.avaliacao || { solicitada: true, respondida: true, nota: notaEscolhida });
      setComentarioAvaliacao("");
      setNotaEscolhida(0);
    } catch (erro) {
      mostrarToastErro(erro instanceof Error ? erro.message : "Não foi possível enviar sua avaliação.");
    } finally {
      setEnviandoAvaliacao(false);
    }
  }

  async function selecionarDestino(proximoDestino: DestinoChatAreaCliente) {
    setDestino(proximoDestino);
    setDigitando(true);
    try {
      const detalhe = await abrirConversaAreaCliente(proximoDestino);
      aplicarConversaAreaCliente(detalhe);
      return detalhe;
    } catch (erro) {
      setDestino(null);
      mostrarToastErro(erro instanceof Error ? erro.message : "Não foi possível abrir o atendimento.");
      return null;
    } finally {
      setDigitando(false);
    }
  }

  /* O cliente encerra o atendimento e o painel volta para a escolha do canal,
     pronto para uma nova conversa. */
  async function encerrarAtendimento() {
    if (!conversaId || !destino || encerrando) return;
    setEncerrando(true);
    try {
      await encerrarConversaAreaCliente(conversaId, destino);
      voltarParaOpcoes();
    } catch (erro) {
      mostrarToastErro(erro instanceof Error ? erro.message : "Não foi possível encerrar a conversa.");
    } finally {
      setEncerrando(false);
    }
  }

  function voltarParaOpcoes() {
    setDestino(null);
    setConversaId(null);
    /* Mantém o agente no cabeçalho: só o atendimento escolhido é descartado. */
    void obterAgenteAreaCliente()
      .then((dados) => { aplicarIdentidadeAgente(dados.agente); setDestinosDisponiveis(dados.destinos); })
      .catch(() => null);
    setTexto("");
    setImagem(null);
    setAvaliacao(null);
    setNotaEscolhida(0);
    setComentarioAvaliacao("");
    setMensagens([{ tipo: "bot", texto: MENSAGEM_INICIAL_AREA_CLIENTE }]);
  }

  async function selecionarImagem(arquivo?: File) {
    if (!arquivo) return;
    if (!arquivo.type.startsWith("image/")) {
      mostrarToastErro("Selecione uma imagem PNG, JPG, WEBP ou GIF.");
      return;
    }
    if (arquivo.size > 8 * 1024 * 1024) {
      mostrarToastErro("A imagem deve ter no máximo 8 MB.");
      return;
    }
    try {
      setImagem({ arquivo, preview: await arquivoDataUrl(arquivo) });
    } catch (erro) {
      mostrarToastErro(erro instanceof Error ? erro.message : "Não foi possível ler a imagem.");
    }
  }

  function adicionarMensagens(novas: Mensagem[]) {
    setMensagens((atuais) => {
      const ids = new Set(atuais.flatMap((item) => item.id ? [item.id] : []));
      return [...atuais, ...novas.filter((item) => !item.id || !ids.has(item.id))];
    });
  }

  async function enviar() {
    const prompt = texto.trim();
    if ((!prompt && !imagem) || digitando) return;
    if (modo === "AREA_CLIENTE") {
      let destinoAtual = destino;
      let idAtual = conversaId;
      if (!destinoAtual || !idAtual) {
        destinoAtual = "DUVIDAS";
        const detalhe = await selecionarDestino(destinoAtual);
        idAtual = detalhe?.conversa.id || null;
      }
      if (!idAtual) return;
      setTexto("");
      setDigitando(true);
      try {
        const resposta = await enviarMensagemAreaCliente(idAtual, destinoAtual, {
          mensagem: prompt,
          base64: imagem?.preview,
          nomeArquivo: imagem?.arquivo.name,
        });
        const novas = [resposta.mensagem, resposta.resposta].filter((item): item is MensagemIA => Boolean(item)).map(converterMensagem);
        adicionarMensagens(novas);
        if (resposta.agente) aplicarIdentidadeAgente(resposta.agente);
        setImagem(null);
      } catch (erro) {
        mostrarToastErro(erro instanceof Error ? erro.message : "Não foi possível enviar a mensagem.");
      } finally {
        setDigitando(false);
      }
      return;
    }
    setTexto("");
    await enviarPergunta(prompt);
  }

  /** Envia uma pergunta ao agente da plataforma (digitada ou vinda de um botão de atalho). */
  async function enviarPergunta(prompt: string) {
    if (!prompt || !conversaId || digitando) return;
    setMensagens((lista) => [...lista, { texto: prompt, tipo: "user" }]);
    setDigitando(true);
    try {
      const resposta = await perguntarPlataformaIA(conversaId, prompt);
      setMensagens((lista) => [...lista, converterMensagem(resposta.mensagem)]);
    } catch (erro) {
      const mensagemFallback = (erro as { payload?: { mensagem?: MensagemIA } })?.payload?.mensagem;
      if (mensagemFallback) setMensagens((lista) => [...lista, converterMensagem(mensagemFallback)]);
      mostrarToastErro(erro instanceof Error ? erro.message : t("assistantSendError"));
    } finally { setDigitando(false); }
  }

  function iniciarArrasto(event: React.PointerEvent) {
    const base = pos ?? { x: window.innerWidth - 360, y: window.innerHeight - 540 };
    arrasto.current = { dx: event.clientX - base.x, dy: event.clientY - base.y };
    const mover = (ev: PointerEvent) => {
      if (!arrasto.current) return;
      const x = Math.max(8, Math.min(window.innerWidth - 60, ev.clientX - arrasto.current.dx));
      const y = Math.max(8, Math.min(window.innerHeight - 60, ev.clientY - arrasto.current.dy));
      setPos({ x, y });
    };
    const soltar = () => {
      arrasto.current = null;
      window.removeEventListener("pointermove", mover);
      window.removeEventListener("pointerup", soltar);
    };
    window.addEventListener("pointermove", mover);
    window.addEventListener("pointerup", soltar);
  }

  const estiloPainel = pos ? { left: pos.x, top: pos.y, right: "auto", bottom: "auto" } : undefined;

  if (!aberto) return null;

  return createPortal(
    <div className={`${styles.painel} ${modo === "AREA_CLIENTE" ? styles.painelArea : ""}`} style={estiloPainel}>
      <div className={styles.header} onPointerDown={iniciarArrasto}>
        {modo === "AREA_CLIENTE" && destino && (
          <button type="button" className={styles.voltar} aria-label="Voltar às opções"
            onPointerDown={(event) => event.stopPropagation()} onClick={voltarParaOpcoes}>
            <IconeVoltar />
          </button>
        )}
        <span className={styles.headerAvatar}>
          {avatar
            ? <BancoImage src={avatar} alt={nome} />
            : <IconeChatGPT width={22} height={22} aria-hidden="true" />}
        </span>
        <div className={styles.headerTexto}>
          <div className={styles.headerNome}>{nome}</div>
          <div className={styles.headerSub}>
            {modo === "AREA_CLIENTE"
              ? destino === "SUPORTE" ? "Suporte técnico"
                : destino === "VENDAS" ? "Atendimento comercial"
                  : destino === "PRODUCAO" ? "Vendas de DTF"
                    : destino === "DUVIDAS" ? "Respostas pela base de conhecimento"
                    : "Normalmente respondemos em minutos"
              : t("assistantIa")}
          </div>
        </div>
        {modo === "AREA_CLIENTE" && destino && conversaId && (
          <button type="button" className={styles.encerrar} disabled={encerrando}
            onPointerDown={(event) => event.stopPropagation()} onClick={() => void encerrarAtendimento()}>
            {encerrando ? "Encerrando..." : "Encerrar"}
          </button>
        )}
        <span onPointerDown={(event) => event.stopPropagation()}><BotaoFechar className={styles.fechar} onClick={onFechar} /></span>
      </div>

      <div className={styles.mensagens} ref={corpoRef}>
        {mensagens.map((mensagem, indice) => (
          <div
            key={mensagem.id || indice}
            className={`${styles.msgRow} ${mensagem.tipo === "user" ? styles.msgRowUser : styles.msgRowBot}`}
          >
            {mensagem.tipo === "bot" && (
              <span className={`${styles.msgAvatar} ${styles.msgAvatarBot}`}>
                {avatar ? <BancoImage src={avatar} alt="" /> : <IconeChatGPT width={22} height={22} aria-hidden="true" />}
              </span>
            )}
            <div className={`${styles.msg} ${mensagem.tipo === "user" ? styles.msgUser : styles.msgBot}`}>
              <span>{mensagem.texto}</span>
              {mensagem.anexos?.map((anexo) => (
                <a key={`${mensagem.id || indice}-${anexo.id}-${anexo.url}`} className={styles.anexoMensagem}
                  href={anexo.url} target="_blank" rel="noreferrer" aria-label={`Abrir imagem ${anexo.nome}`}>
                  <BancoImage src={anexo.url} alt={anexo.nome} />
                </a>
              ))}
            </div>
            {mensagem.tipo === "user" && (
              <span className={`${styles.msgAvatar} ${styles.msgAvatarUser}`}>
                {avatarUsuario ? <BancoImage src={avatarUsuario} alt={nomeUsuarioAtual || "Usuário"} /> : <span>{inicialUsuario}</span>}
              </span>
            )}
          </div>
        ))}
        {modo === "PLATAFORMA" && perguntasRapidas.length > 0 && !mensagens.some((item) => item.tipo === "user") && (
          <div className={styles.opcoes} aria-label={t("assistantIa")}>
            {perguntasRapidas.map((pergunta) => (
              <button key={pergunta} type="button" disabled={!conversaId || digitando}
                onClick={() => void enviarPergunta(pergunta)}>
                {pergunta}
              </button>
            ))}
          </div>
        )}
        {modo === "AREA_CLIENTE" && !destino && (
          <div className={styles.opcoes} aria-label="Opções de atendimento">
            {OPCOES_ATENDIMENTO
              .filter((opcao) => destinosDisponiveis.includes(opcao.destino))
              .map((opcao) => (
                <button key={opcao.destino} type="button" onClick={() => void selecionarDestino(opcao.destino)}>
                  {opcao.rotulo}
                </button>
              ))}
          </div>
        )}
        {modo === "AREA_CLIENTE" && avaliacao?.solicitada && !avaliacao.respondida && (
          <div className={styles.avaliacao}>
            <strong>Como foi o seu atendimento?</strong>
            <div className={styles.avaliacaoNotas}>
              {NOTAS_AVALIACAO.map((item) => (
                <button
                  key={item.valor}
                  type="button"
                  className={notaEscolhida === item.valor ? styles.avaliacaoNotaAtiva : undefined}
                  aria-label={item.rotulo}
                  aria-pressed={notaEscolhida === item.valor}
                  disabled={enviandoAvaliacao}
                  onClick={() => setNotaEscolhida(item.valor)}
                >
                  <span aria-hidden="true">{item.emoji}</span>
                  <small>{item.rotulo}</small>
                </button>
              ))}
            </div>
            <textarea
              className={styles.avaliacaoComentario}
              value={comentarioAvaliacao}
              maxLength={1000}
              rows={2}
              placeholder="Quer comentar algo? (opcional)"
              disabled={enviandoAvaliacao}
              onChange={(event) => setComentarioAvaliacao(event.target.value)}
            />
            <button
              type="button"
              className={styles.avaliacaoEnviar}
              disabled={!notaEscolhida || enviandoAvaliacao}
              onClick={() => void enviarAvaliacao()}
            >
              {enviandoAvaliacao ? "Enviando..." : "Enviar avaliação"}
            </button>
          </div>
        )}
        {modo === "AREA_CLIENTE" && avaliacao?.respondida && (
          <div className={styles.avaliacaoObrigado}>Obrigado pela sua avaliação!</div>
        )}
        {(digitando || atendenteDigitando) && (
          <div className={`${styles.msgRow} ${styles.msgRowBot}`}>
            <span className={`${styles.msgAvatar} ${styles.msgAvatarBot}`}>
              {avatar ? <BancoImage src={avatar} alt="" /> : <IconeChatGPT width={22} height={22} aria-hidden="true" />}
            </span>
            <div className={`${styles.msg} ${styles.msgBot} ${styles.digitando}`}>
              <span /><span /><span />
            </div>
          </div>
        )}
      </div>

      <div className={styles.compositor}>
        {imagem && (
          <div className={styles.imagemSelecionada}>
            <BancoImage src={imagem.preview} alt="Imagem selecionada" />
            <span>{imagem.arquivo.name}</span>
            <button type="button" aria-label="Remover imagem" onClick={() => setImagem(null)}>×</button>
          </div>
        )}
        <div className={styles.inputArea}>
          <CampoPesquisa
            className={styles.inputField}
            inputClassName={styles.input}
            iconeInicio={null}
            placeholder="Digite sua mensagem..."
            value={texto}
            onChange={(event) => { setTexto(event.target.value); avisarDigitando(); }}
            onKeyDown={(event) => { if (event.key === "Enter") void enviar(); }}
            disabled={(modo === "PLATAFORMA" && !conversaId) || digitando}
          />
          {/* Mesmo seletor de emojis do Atendimento de Tickets. O painel é
              renderizado em portal: o corpo do chat tem overflow oculto e
              cortaria a lista. */}
          <div className={styles.acaoEmoji}>
            <button ref={botaoEmojiRef} className={styles.acao} type="button" aria-label="Emojis" disabled={digitando}
              onClick={alternarEmojis}>
              <Smile size={18} strokeWidth={1.8} />
            </button>
          </div>
          {modo === "AREA_CLIENTE" && (
            <>
              <input ref={inputArquivoRef} className={styles.inputArquivo} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                onChange={(event) => { void selecionarImagem(event.target.files?.[0]); event.currentTarget.value = ""; }} />
              <button className={styles.acao} type="button" aria-label="Anexar imagem" disabled={digitando}
                onClick={() => inputArquivoRef.current?.click()}>
                <IconeClipe />
              </button>
            </>
          )}
          <button className={`${styles.acao} ${styles.enviar}`} onClick={() => void enviar()} aria-label={t("sendMessage")}
            disabled={(!texto.trim() && !imagem) || (modo === "PLATAFORMA" && !conversaId) || digitando}>
            <IconeEnviar />
          </button>
        </div>
      </div>
      {/* Fora do corpo do chat (que recorta o conteúdo) e alinhado à esquerda
          do botão, para o painel nunca ficar cortado. */}
      {emojiAberto && posicaoEmojis && (
        <div
          ref={ancoraEmojisRef}
          className={styles.ancoraEmojis}
          style={{ left: posicaoEmojis.left, bottom: posicaoEmojis.bottom }}
        >
          <PainelEmojis
            paraCima
            className={styles.painelEmojisFlutuante}
            onSelecionar={(emoji) => setTexto((atual) => `${atual}${emoji}`)}
            onFechar={() => setEmojiAberto(false)}
          />
        </div>
      )}
    </div>,
    document.body
  );
}

function converterMensagem(mensagem: MensagemIA): Mensagem {
  return {
    id: mensagem.id,
    texto: mensagem.conteudo,
    tipo: ["IA", "SUPORTE", "SISTEMA"].includes(mensagem.autor_tipo) ? "bot" : "user",
    anexos: mensagem.anexos,
  };
}
